import { createApi } from '@reduxjs/toolkit/query/react';
import baseQueryWithReauth from './base';
import { Response } from '../../models/shared';
import {
  EventCharge, CreateEventChargeDTO, UpdateEventChargeDTO,
} from '../../models/event-charge';
import { eventsApi } from './events';
import { paymentsApi } from './payment';

// Charges feed both the event balance (eventsApi) and the revenue report
// (paymentsApi), so a mutation invalidates those caches too.
const refreshDependentReports = async (
  _arg: unknown,
  { dispatch, queryFulfilled }: { dispatch: (action: unknown) => void; queryFulfilled: Promise<unknown> },
) => {
  try {
    await queryFulfilled;
    dispatch(eventsApi.util.invalidateTags([{ type: 'events' }]));
    dispatch(paymentsApi.util.invalidateTags(['payments']));
  } catch (e) {
    console.error(e);
  }
};

const chargesApi = createApi({
  reducerPath: 'chargesApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['charges'],
  endpoints: (builder) => ({
    getEventCharges: builder.query<EventCharge[], { eventId: string }>({
      query: ({ eventId }) => ({
        url: `/events/${eventId}/charges`,
        method: 'GET',
      }),
      providesTags: ['charges'],
      transformResponse: (response: Response<EventCharge[]>) => response.data || [],
    }),
    createEventCharge: builder.mutation<Response<EventCharge>, { eventId: string; charge: CreateEventChargeDTO }>({
      query: ({ eventId, charge }) => ({
        url: `/events/${eventId}/charges`,
        method: 'POST',
        body: charge,
      }),
      invalidatesTags: ['charges'],
      onQueryStarted: refreshDependentReports,
    }),
    updateEventCharge: builder.mutation<Response<EventCharge>, { eventId: string; chargeId: string; charge: UpdateEventChargeDTO }>({
      query: ({ eventId, chargeId, charge }) => ({
        url: `/events/${eventId}/charges/${chargeId}`,
        method: 'PUT',
        body: charge,
      }),
      invalidatesTags: ['charges'],
      onQueryStarted: refreshDependentReports,
    }),
    deleteEventCharge: builder.mutation<Response<null>, { eventId: string; chargeId: string }>({
      query: ({ eventId, chargeId }) => ({
        url: `/events/${eventId}/charges/${chargeId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['charges'],
      onQueryStarted: refreshDependentReports,
    }),
  }),
});

export const {
  useGetEventChargesQuery,
  useCreateEventChargeMutation,
  useUpdateEventChargeMutation,
  useDeleteEventChargeMutation,
} = chargesApi;
export { chargesApi };
